"use client";

import { SlidersHorizontal, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState, useTransition } from "react";

import { availabilityOptions, productTypeOptions } from "@/data/catalog-ui-content";
import { panelOverlay, subtleTween, usePressedInteraction } from "@/components/ui/motion-primitives";

export interface FilterButtonProps {
  readonly activeCount: number;
}

export function FilterButton({ activeCount }: Readonly<FilterButtonProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const interaction = usePressedInteraction();

  return (
    <>
      <motion.button
        className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-primary bg-primary px-3 text-sm font-extrabold text-white outline-none transition-colors hover:bg-primary-container focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        onClick={() => setIsOpen(true)}
        type="button"
        {...interaction}
      >
        <SlidersHorizontal aria-hidden="true" size={16} />
        Filtros{activeCount > 0 ? ` (${activeCount})` : ""}
      </motion.button>
      <AnimatePresence>
        {isOpen ? <FilterPanel key="catalog-filter-panel" onClose={() => setIsOpen(false)} /> : null}
      </AnimatePresence>
    </>
  );
}

interface FilterPanelProps {
  readonly onClose: () => void;
}

function FilterPanel({ onClose }: Readonly<FilterPanelProps>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [productType, setProductType] = useState(searchParams.get("productType") ?? "");
  const [availability, setAvailability] = useState(searchParams.get("availability") ?? "");
  const [isApplying, setIsApplying] = useState(false);
  const [isPending, startTransition] = useTransition();
  const isBusy = isApplying || isPending;

  function applyFilters() {
    if (isBusy) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    setOptionalParam(nextParams, "productType", productType);
    setOptionalParam(nextParams, "availability", availability);
    setIsApplying(true);
    startTransition(() => {
      router.push(`${pathname}?${nextParams.toString()}`);
    });
    window.setTimeout(onClose, 180);
  }

  return (
    <motion.div
      animate="visible"
      aria-labelledby="catalog-filter-title"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/30 px-4 pt-20"
      exit="exit"
      initial="hidden"
      role="dialog"
      transition={subtleTween}
      variants={panelOverlay}
    >
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-md rounded-xl bg-surface p-4"
        exit={{ opacity: 0, y: 8 }}
        initial={{ opacity: 0, y: 10 }}
        transition={subtleTween}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-primary" id="catalog-filter-title">
            Filtros
          </h2>
          <button
            aria-label="Cerrar filtros"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary"
            disabled={isBusy}
            onClick={onClose}
            type="button"
          >
            <X aria-hidden="true" size={22} />
          </button>
        </div>

        <label className="mt-4 block text-sm font-bold text-foreground">
          Tipo
          <select
            className="mt-2 h-11 w-full rounded-lg border border-outline-variant bg-surface px-3 outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"
            disabled={isBusy}
            onChange={(event) => setProductType(event.target.value)}
            value={productType}
          >
            <option value="">Todos</option>
            {productTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-4 block text-sm font-bold text-foreground">
          Disponibilidad
          <select
            className="mt-2 h-11 w-full rounded-lg border border-outline-variant bg-surface px-3 outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"
            disabled={isBusy}
            onChange={(event) => setAvailability(event.target.value)}
            value={availability}
          >
            <option value="">Todas</option>
            {availabilityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            className="min-h-11 rounded-lg border border-outline-variant font-extrabold text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary"
            disabled={isBusy}
            onClick={() => {
              setProductType("");
              setAvailability("");
            }}
            type="button"
          >
            Limpiar
          </button>
          <button
            aria-busy={isBusy}
            className="min-h-11 rounded-lg bg-primary font-extrabold text-white outline-none hover:bg-primary-container focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            disabled={isBusy}
            onClick={applyFilters}
            type="button"
          >
            {isBusy ? "Aplicando..." : "Aplicar"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function setOptionalParam(params: URLSearchParams, key: string, value: string) {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}
